import { Button, Tooltip, TooltipContent, TooltipTrigger } from "@aircall/ds";
import { Fullscreen, X } from "@aircall/react-icons";
import { useTranslation } from "react-i18next";

import { useCall } from "@/hooks/useCall/useCall";
import { useTrack } from "@/hooks/useTrack/useTrack";
import { useBaseStore } from "@/store/store";

import { PLAYER_ACTION_BUTTON_SIZE } from "./Player.constants";

export const fullScreenIconId = "full-screen-icon";
export const closeFullScreenIconId = "close-full-screen-icon";

/**
 * Icon-only toggle that expands the transcript view to full screen and exits it
 * again. Both directions render at PLAYER_ACTION_BUTTON_SIZE, the same size
 * token as the view picker sitting beside them in the player action row.
 */
export function PlayerFullScreenToggle() {
  const { track } = useTrack();
  const { t } = useTranslation();

  const { call } = useCall();

  const isExpanded = useBaseStore((store) => store.isExpanded);
  const toggle = useBaseStore((store) => store.setIsExpanded);

  const handleOnClick = () => {
    if (!isExpanded) {
      track("transcript_view_expanded", {
        call_id: call?.id,
        call_uuid: call?.uuid,
      });
    }

    toggle();
  };

  const { Icon, dataTest, tooltip } = isExpanded
    ? { Icon: X, dataTest: closeFullScreenIconId, tooltip: "close" }
    : {
        Icon: Fullscreen,
        dataTest: fullScreenIconId,
        tooltip: "full_screen",
      };

  return (
    // The player container turns off pointer events while it has no playable
    // audio (see Player.styled), so the toggle has to opt back in to stay usable
    // in that state.
    <div className="pointer-events-auto">
      <Tooltip>
        <TooltipTrigger
          render={
            <Button
              variant="ghost"
              size={PLAYER_ACTION_BUTTON_SIZE}
              onClick={handleOnClick}
              data-test={dataTest}
              aria-label={t(tooltip)}
            />
          }
        >
          <Icon />
        </TooltipTrigger>
        <TooltipContent>{t(tooltip)}</TooltipContent>
      </Tooltip>
    </div>
  );
}
